import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { saveContactSubmission } from './firebase';
import './Support.css';

const Support = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        category: 'General Inquiry',
        message: ''
    });
    const [formStatus, setFormStatus] = useState({ type: '', message: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
        document.title = "Support - Albo Labs";
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setFormStatus({ type: '', message: '' });

        // Basic validation
        if (!formData.name || !formData.email || !formData.message) {
            setFormStatus({
                type: 'error',
                message: 'Please fill in all required fields.'
            });
            setIsSubmitting(false);
            return;
        }

        const result = await saveContactSubmission({
            ...formData,
            source: 'Support Page'
        });

        if (result.success) {
            setFormStatus({
                type: 'success',
                message: 'Support ticket submitted successfully! We\'ll get back to you soon.'
            });
            setFormData({
                name: '',
                email: '',
                category: 'General Inquiry',
                message: ''
            });
        } else {
            setFormStatus({
                type: 'error',
                message: 'Failed to submit. Please try again or email us directly at support@albolabs.com.'
            });
        }
        setIsSubmitting(false);
    };

    const categories = [
        { icon: '🛠️', title: 'Technical Support', description: 'Assistance with bugs, installation, or platform issues.' },
        { icon: '💳', title: 'Billing & Account', description: 'Manage your subscription, invoices, or account settings.' },
        { icon: '🚀', title: 'Product Inquiries', description: 'Questions about features, capabilities, or custom solutions.' },
        { icon: '💬', title: 'General Support', description: 'Anything else you need help with.' }
    ];

    return (
        <div className="support-container">
            <header className="support-hero">
                <div className="container">
                    <Link to="/" className="back-link">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="19" y1="12" x2="5" y2="12"></line>
                            <polyline points="12 19 5 12 12 5"></polyline>
                        </svg>
                        Back to Home
                    </Link>
                    <h1>How can we help?</h1>
                    <p className="hero-subtitle">Get the support you need to build clean and think clear.</p>
                </div>
            </header>

            <section className="support-categories container">
                <div className="categories-grid">
                    {categories.map((cat, index) => (
                        <div key={index} className="category-card" onClick={() => setFormData(p => ({ ...p, category: cat.title }))}>
                            <div className="category-icon">{cat.icon}</div>
                            <h3>{cat.title}</h3>
                            <p>{cat.description}</p>
                        </div>
                    ))}
                </div>
            </section>

            <section className="support-form-section container">
                <div className="form-card">
                    <h2>Submit a Support Ticket</h2>
                    <p>Fill out the form below and our team will get back to you within 24 hours.</p>

                    <form onSubmit={handleSubmit} className="support-form">
                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="name">Name *</label>
                                <input
                                    type="text"
                                    id="name"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    placeholder="Your full name"
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="email">Email *</label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    placeholder="your.email@example.com"
                                    required
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label htmlFor="category">Inquiry Type</label>
                            <select
                                id="category"
                                name="category"
                                value={formData.category}
                                onChange={handleInputChange}
                            >
                                {categories.map((cat, i) => (
                                    <option key={i} value={cat.title}>{cat.title}</option>
                                ))}
                            </select>
                        </div>

                        <div className="form-group">
                            <label htmlFor="message">Message *</label>
                            <textarea
                                id="message"
                                name="message"
                                value={formData.message}
                                onChange={handleInputChange}
                                placeholder="Describe your issue or question in detail..."
                                rows="6"
                                required
                            ></textarea>
                        </div>

                        {formStatus.message && (
                            <div className={`form-status ${formStatus.type}`}>
                                {formStatus.message}
                            </div>
                        )}

                        <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                            {isSubmitting ? 'Sending...' : 'Send Message'}
                            {!isSubmitting && <span>→</span>}
                        </button>
                    </form>
                </div>
            </section>

            <section className="support-info container">
                <div className="info-grid">
                    <div className="info-item">
                        <h4>📧 Email Us</h4>
                        <p>Prefer direct email? Reach us at <a href="mailto:support@albolabs.com">support@albolabs.com</a></p>
                    </div>
                    <div className="info-item">
                        <h4>🌐 Knowledge Base</h4>
                        <p>Explore our documentation and tutorials for quick answers.</p>
                    </div>
                </div>
            </section>

            <footer className="support-footer">
                <div className="container">
                    <p>&copy; {new Date().getFullYear()} Albo Labs. All rights reserved.</p>
                </div>
            </footer>
        </div>
    );
};

export default Support;
